import { useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { Box, Typography, useTheme, alpha } from "@mui/material";
import { DonutLarge, BarChart as BarIcon } from "@mui/icons-material";
import Grid from "@mui/material/Grid2";
import { useResponsive } from "../../../core/hooks/useResponsive";

const num = (v) => {
  if (v == null || v === "") return null;
  const n = Number(String(v).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : null;
};

const TradingDeskCharts = ({ summary, boughtList }) => {
  const theme = useTheme();
  const { isPhone } = useResponsive();
  const primary = theme.palette.primary.main;
  const profitC = theme.palette.profitColor ?? "#16a34a";
  const lossC = theme.palette.lossColor ?? "#dc2626";

  const rows = Array.isArray(boughtList) ? boughtList : [];

  const budgetValues = useMemo(() => {
    const budget = num(summary?.tradeBudget);
    const balance = num(summary?.tradeBalance);
    if (budget == null || balance == null || budget <= 0) {
      return { budget: budget ?? 0, deployed: 0, remaining: balance ?? 0, utilizedPct: 0, remainingPct: 0 };
    }
    const deployed = Math.max(0, budget - balance);
    const remaining = balance;
    const utilizedPct = Math.max(0, Math.min(100, (deployed / budget) * 100));
    const remainingPct = Math.max(0, Math.min(100, (remaining / budget) * 100));
    return { budget, deployed, remaining, utilizedPct, remainingPct };
  }, [summary]);

  const budgetDonut = useMemo(() => {
    const utilizedPct = budgetValues?.utilizedPct ?? 0;
    const remainingPct = budgetValues?.remainingPct ?? 0;
    const deployed = budgetValues?.deployed ?? 0;
    const remaining = budgetValues?.remaining ?? 0;

    if (utilizedPct === 0 && remainingPct === 0) return [];
    return [
      { name: "Available", value: utilizedPct, actualValue: deployed, color: primary },
      { name: "Remaining", value: remainingPct, actualValue: remaining, color: alpha(primary, 0.35) },
    ];
  }, [budgetValues, primary]);

  const totals = useMemo(() => {
    const profit = rows.reduce((sum, r) => {
      const n = num(r.profitLoss);
      return sum + (n != null && n > 0 ? n : 0);
    }, 0);
    const loss = rows.reduce((sum, r) => {
      const n = num(r.profitLoss);
      return sum + (n != null && n < 0 ? Math.abs(n) : 0);
    }, 0);

    return {
      totalProfit: profit,
      totalLoss: loss,
    };
  }, [rows]);

  const totalChartData = useMemo(() => {
    const profitVal = num(totals.totalProfit) ?? 0;
    const lossVal = num(totals.totalLoss) ?? 0;
    return [
      { name: "Total Profit", value: profitVal, color: profitC },
      { name: "Total Loss", value: lossVal, color: lossVal > 0 ? lossC : alpha(lossC, 0.35) },
    ];
  }, [totals.totalProfit, totals.totalLoss, profitC, lossC]);

  const hasBudget = budgetDonut.length > 0;
  const hasTotals = totalChartData.some((d) => (d.value ?? 0) > 0) || totalChartData.length > 0;

  if (!hasBudget && !hasTotals) {
    return (
      <Box sx={{ py: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Charts appear as budget and position data stream in.
        </Typography>
      </Box>
    );
  }

  const pieCard = (title, subtitle, children) => (
    <Box
      sx={{
        flex: "1 1 240px",
        minWidth: 0,
        borderRadius: 2,
        height: "100%",
        border: `1px solid ${alpha(primary, 0.12)}`,
        bgcolor: alpha(theme.palette.background.paper, 0.9),
        p: { xs: 1, sm: 1.25 },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
        <DonutLarge sx={{ color: primary, fontSize: 20 }} />
        <Typography variant="subtitle2" fontWeight={700}>
          {title}
        </Typography>
      </Box>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          {subtitle}
        </Typography>
      )}
      {children}
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      <Grid container spacing={1.25}>
        <Grid size={{ xs: 12, md: 6 }}>
          {hasBudget &&
            pieCard(
              "Budget use",
              "Available vs Remaining",
              <ResponsiveContainer width="100%" height={isPhone ? 175 : 200}>
                <PieChart>
                  <Pie
                    data={budgetDonut}
                    cx="50%"
                    cy="50%"
                    innerRadius={isPhone ? 40 : 48}
                    outerRadius={isPhone ? 60 : 72}
                    dataKey="value"
                    nameKey="name"
                    paddingAngle={2}
                  >
                    {budgetDonut.map((e, i) => (
                      <Cell
                        key={i}
                        fill={e.color}
                        stroke={theme.palette.background.paper}
                        strokeWidth={2}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active }) => {
                      if (!active) return null;
                      const utilized = budgetValues?.deployed ?? 0;
                      const remaining = budgetValues?.remaining ?? 0;
                      if (!utilized && !remaining) return null;
                      return (
                        <Box
                          sx={{
                            bgcolor: "background.paper",
                            p: 1,
                            borderRadius: 1,
                            border: `1px solid ${alpha(primary, 0.18)}`,
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 800, color: primary }}>
                            Budget use
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.25 }}>
                            Utilized: ₹{Number(utilized).toLocaleString()}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.25 }}>
                            Remaining: ₹{Number(remaining).toLocaleString()}
                          </Typography>
                        </Box>
                      );
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          {hasTotals && (
            <Box
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(primary, 0.12)}`,
                bgcolor: alpha(theme.palette.background.paper, 0.9),
                p: 1.25,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                <BarIcon sx={{ color: primary, fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight={700}>
                  Total P/L
                </Typography>
              </Box>
              <ResponsiveContainer width="100%" height={isPhone ? 175 : 200}>
                <BarChart
                  data={totalChartData}
                  margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={alpha(theme.palette.divider, 0.8)}
                  />
                  <XAxis type="category" dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis
                    type="number"
                    width={isPhone ? 44 : 60}
                    tick={{ fontSize: 10 }}
                    tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`}
                  />
                  <Tooltip formatter={(v) => `₹ ${Number(v).toLocaleString()}`} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={120}>
                    {totalChartData.map((e, i) => (
                      <Cell key={i} fill={e.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default TradingDeskCharts;
